import { AppButton } from '@clickguard/design-system'
import { useState, FormEvent, ChangeEvent } from 'react'
import { useParams } from 'react-router-dom'
import { GInput } from 'components/basic-blocks'
import { ModalTypes } from 'components/basic-blocks/g-modal/types'
import { useStores } from 'hooks'
import { useUiStore, useUserStore, useAccountStore } from 'store'
import { parseError } from 'utils/errors'
import { toast } from 'react-toastify'
import Joi from 'joi'
import { useTrackPostHogEvent } from 'utils/posthog'
import { PostHogEvent, PostHogPath } from 'utils/posthog-events'

const inviteSchema = Joi.object({
	email: Joi.string()
		.email({ tlds: { allow: false } })
		.required()
		.label('Email')
})

interface InviteTeamMemberProps {
	step?: 'protect_website' | 'ad_account' | 'tracking_code'
}

export const InviteTeamMember = (
	{ step = 'ad_account' }: InviteTeamMemberProps = { step: 'ad_account' }
) => {
	const { accountId } = useParams<{ accountId: string }>()
	const setModalContent = useUiStore((s) => s.setModalContent)
	const { user } = useUserStore((s) => ({ user: s.user }))
	const { accounts } = useAccountStore((s) => ({
		accounts: s.accounts
	}))
	const userAccess = accounts
		?.find((a) => a.id === accountId)
		?.user_access.find((u) => u.user_id === user?.id)
	if (!userAccess || userAccess.access_level !== 'OWNER') {
		return null
	}

	const renderModal = () => {
		setModalContent({
			title: 'Invite a member to complete this step',
			subtitle:
				'We will send an email for them to create an account and continue the process.',
			type: ModalTypes.INFO,
			actionsEnabled: false,
			showIcon: false,
			body: <ModalBody accountId={accountId} step={step} />
		})
	}

	return (
		<button
			onClick={renderModal}
			className="flex items-center justify-center gap-[5px] text-brand text-[14px] leading-[18px] hover:text-t-email-blue transition-colors cursor-pointer"
		>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				width="16"
				height="16"
				viewBox="0 0 16 16"
				fill="none"
			>
				<path
					d="M14.6673 12L12.6673 10L10.6673 12M12.6673 10V14M10.334 2.19384C11.3113 2.58943 12.0007 3.54754 12.0007 4.66667C12.0007 5.78579 11.3113 6.7439 10.334 7.13949M8.00065 10H5.33398C4.09147 10 3.47022 10 2.98016 10.203C2.32675 10.4736 1.80762 10.9928 1.53697 11.6462C1.33398 12.1362 1.33398 12.7575 1.33398 14M9.00065 4.66667C9.00065 6.13943 7.80674 7.33333 6.33398 7.33333C4.86122 7.33333 3.66732 6.13943 3.66732 4.66667C3.66732 3.19391 4.86122 2 6.33398 2C7.80674 2 9.00065 3.19391 9.00065 4.66667Z"
					style={{ stroke: 'var(--color-brand)' }}
					stroke-width="1.5"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
			</svg>
			<span>Invite a team member to complete this step</span>
		</button>
	)
}

interface ModalBodyProps {
	accountId?: string
	step: 'protect_website' | 'ad_account' | 'tracking_code'
}

const ModalBody = ({ accountId, step }: ModalBodyProps) => {
	const [email, setEmail] = useState('')
	const [error, setError] = useState<string | null>(null)
	const [loading, setLoading] = useState(false)
	const { accountStore } = useStores()
	const { inviteAccountUser } = accountStore
	const setModalState = useUiStore((s) => s.setModalState)
	const trackPostHogEvent = useTrackPostHogEvent()

	const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
		setEmail(e.target.value)
		setError(null)
	}

	const confirmSendInvite = async () => {
		// Validate email
		const validation = inviteSchema.validate({ email })
		if (validation.error) {
			setError(validation.error.details[0].message)
			return
		}

		if (!accountId) {
			setError('Account ID not found')
			return
		}

		setLoading(true)
		setError(null)

		try {
			await inviteAccountUser(accountId, {
				email: email.trim(),
				access_level: 'READ' // Analyst role
			})
			trackPostHogEvent(
				PostHogEvent.ONBOARDING_INVITE_MEMBER,
				PostHogPath.ONBOARDING,
				{ step }
			)
			setModalState(false)
		} catch (err) {
			const errorMessage = parseError(err) || 'Failed to send invitation'
			setError(errorMessage)
			toast.error(errorMessage)
		} finally {
			setLoading(false)
		}
	}

	return (
		<>
			<form
				data-hs-do-not-collect="true"
				onSubmit={(e: FormEvent<HTMLFormElement>) => e.preventDefault()}
			>
				<div className="my-4 w-full">
					<GInput
						type="email"
						name="email"
						label="Email"
						value={email}
						onChange={handleEmailChange}
						placeholder="example@clickguard.com"
						error={error || undefined}
						labelClassName="text-base font-medium"
						className="flex justify-between flex-col gap-1"
						inputClassName="w-full text-base font-normal"
						errorPosition="flex"
					/>
				</div>
			</form>
			<div className="flex justify-end gap-4 mt-5">
				<AppButton
					variant="marketing"
					size="lg"
					fullWidth
					label="send invite"
					isLoading={loading}
					loader="dots"
					disabled={loading || !email.trim()}
					onClick={() => confirmSendInvite()}
				/>
			</div>
		</>
	)
}
